import { Button, Card, Image, Space, Table, Typography, message } from "antd";
import { getMediaUrl } from 'config/env';
import Loading from 'layouts/Loading/Loading';
import Axios from 'middleware/axiosInstance';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './ImageGallerySorting.scss';

const { Title, Text } = Typography;

const ImageGallerySorting = () => {
    const navigate = useNavigate();

    const [allImageGalleries, setAllImageGalleries] = useState([]);
    const [hasOrderChanged, setHasOrderChanged] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const getAllImageGalleries = () => {
        Axios.get("/image-gallery")
            .then((res) => {
                setAllImageGalleries(res.data || []);
            })
            .catch(err => {
                console.log(err);
                message.error('خطا در بارگذاری لیست تصاویر گالری');
            });
    };

    useEffect(() => {
        getAllImageGalleries();
    }, []);

    // Handle drag and drop with HTML5 API
    const handleDragStart = (e, index) => {
        e.dataTransfer.setData('text/plain', index);
        e.target.style.opacity = '0.5';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
        
        if (dragIndex !== dropIndex) {
            const newImageGalleries = [...allImageGalleries];
            const [draggedItem] = newImageGalleries.splice(dragIndex, 1);
            newImageGalleries.splice(dropIndex, 0, draggedItem);
            
            setAllImageGalleries(newImageGalleries);
            setHasOrderChanged(true);
        }
        
        e.target.style.opacity = '1';
    };

    const handleDragEnd = (e) => {
        e.target.style.opacity = '1';
    };

    // Submit new order to backend
    const submitNewOrder = async () => {
        if (!hasOrderChanged) {
            message.info('ترتیب تغییر نکرده است');
            return;
        }

        setIsSubmitting(true);
        try {
            const orderData = allImageGalleries.map((imageGallery, index) => ({
                id: imageGallery.id,
                order: index + 1
            }));

            const response = await Axios.post('/image-gallery/reorder', orderData);
            
            if (response.status === 200) {
                message.success('ترتیب با موفقیت ذخیره شد');
                setHasOrderChanged(false);
            }
        } catch (error) {
            console.error('Error updating order:', error);
            message.error('خطا در ذخیره ترتیب');
        } finally {
            setIsSubmitting(false);
        }
    };

    const columns = [
        {
            title: "ترتیب",
            dataIndex: "order",
            key: "order",
            width: 80,
            render: (_, record, index) => (
                <div className="order-number">
                    {index + 1}
                </div>
            ),
        },
        {
            title: "تصویر",
            dataIndex: "image",
            key: "image",
            width: 100,
            render: (image, record) => (
                image ? (
                    <Image
                        width={60}
                        height={60}
                        src={getMediaUrl(image)}
                        alt={record.title_fa}
                        style={{ objectFit: 'cover', borderRadius: '4px' }}
                        preview={false}
                    />
                ) : (
                    <span>-</span>
                )
            ),
        },
        {
            title: "عنوان فارسی",
            dataIndex: "title_fa",
            key: "title_fa",
            sorter: (a, b) =>
                a.title_fa > b.title_fa ? 1 : b.title_fa > a.title_fa ? -1 : 0,
            ellipsis: true,
        },
        {
            title: "عنوان انگلیسی",
            dataIndex: "title_en",
            key: "title_en",
            ellipsis: true,
        },
    ];

    return (
        <div className="customized-container image-gallery-sorting-page">
            <div className="customized-wrapper">
                <Card>
                    <div className="page-header">
                        <Title level={3}>مرتب‌سازی تصاویر گالری</Title>
                        <Text type="secondary">
                            برای تغییر ترتیب تصاویر گالری، روی هر ردیف کلیک کرده و آن را به موقعیت جدید بکشید
                        </Text>
                    </div>

                    <div className="action-buttons">
                        <Space>
                            <Button 
                                type="primary" 
                                onClick={submitNewOrder}
                                loading={isSubmitting}
                                disabled={!hasOrderChanged}
                            >
                                ذخیره ترتیب جدید
                            </Button>
                            <Button 
                                onClick={() => {
                                    setHasOrderChanged(false);
                                    getAllImageGalleries();
                                }}
                            >
                                بازنشانی
                            </Button>
                            <Button 
                                type="default" 
                                onClick={() => navigate('/image-galleries')}
                            >
                                بازگشت به لیست تصاویر گالری
                            </Button>
                        </Space>
                    </div>

                    {/* Info */}
                    {hasOrderChanged && (
                        <div className="info-alert">
                            <Text type="success">
                                ⚠️ ترتیب تغییر کرده است. برای ذخیره تغییرات روی "ذخیره ترتیب جدید" کلیک کنید.
                            </Text>
                        </div>
                    )}

                    {allImageGalleries.length > 0 ? (
                        <div className="sorting-table">
                            <Table
                                pagination={false}
                                columns={columns}
                                dataSource={allImageGalleries}
                                rowKey="id"
                                onRow={(record, index) => ({
                                    draggable: true,
                                    onDragStart: (e) => handleDragStart(e, index),
                                    onDragOver: (e) => handleDragOver(e),
                                    onDrop: (e) => handleDrop(e, index),
                                    onDragEnd: handleDragEnd,
                                    style: { cursor: 'grab' },
                                    className: 'draggable-row',
                                })}
                            />
                        </div>
                    ) : (
                        <div className="loading-container">
                            <Loading />
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default ImageGallerySorting;

